import * as React from "react";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { Dropdown } from "../../bits/Dropdown/Dropdown";
import FilterButton from "../../bits/FilterButton/FilterButton";
import styles from "./Caseload.module.scss";
import {
    BookingStatus,
    DateFormats,
    IClient,
    IClientBooking,
    IClinician,
    RecurrenceRule,
} from "intake_shared/src/types";
import { CacheContext } from "../../../../contexts/CacheContext";
import { AclContext } from "../../../../contexts/AclContext";
import { SearchInput } from "../../bits/SearchInput/SearchInput";
import { TableHeader } from "../../../../types";
import { findLabelByPracticeId, getStateDisplayName } from "../../../../utils/utils";
import { Badge } from "../../bits/Badge/Badge";
import moment from "moment";
import { getV2BadgePropsByPractice } from "../../../../constants";
import { serviceDeliveryOptionLabels } from "intake_shared/src/constants";
import { useToasts } from "react-toast-notifications";
import store from "../../../../store";
import { Button } from "../../bits/Button/Button";
import Table from "../../bits/Table/Table";
import ChevronDownIcon from "../../icons/ChevronDown";
import { isClientElderly, isClientMinor } from "../../../../utils/age";
import { AlertDialog } from "../../bits/AlertDialog/AlertDialog";
import { ClinicianAddBooking } from "../ClinicianAddBooking/ClinicianAddBooking";
import ClientDisplayDialog, {
    defaultClient,
} from "../../clients/ClientDisplayDialog/ClientDisplayDialog";
import { Dialog } from "../../bits/Dialog/Dialog";
import EmptyListState from "../../bits/EmptyListState/EmptyListState";
import PauseIcon from "../../icons/Pause";
import { DepartmentCache } from "../../../../store/cache/requested_service";
import { ClinicianServiceSelection } from "../../../../store/department";
import { sendServiceTypeChangeEmail } from "../../../../utils/queue_email";
import { Select } from "../../bits/Select/Select";
import { getClinicianCaseloadStats } from "../../../v2utils";
import { InfoIcon } from "../../icons/Info";

interface CaseloadProps {
    clinician: IClinician;
    caseloadClinician: IClinician;
    clients: { [id: string]: IClient };
    setCaseLoadCountByFilter: (value: React.SetStateAction<number>) => void;
    onUpdateCaseLoad: () => Promise<void>;
    clinicianServiceSelections: ClinicianServiceSelection;
}

type Dialogs =
    | "cancel_session"
    | "switch_to_telemed"
    | "view_consult_call"
    | "request_service_type_change";

type Option = {
    value: string;
    label: string;
};

export default function Caseload({
    clinician,
    caseloadClinician,
    clients,
    setCaseLoadCountByFilter,
    onUpdateCaseLoad,
    clinicianServiceSelections,
}: CaseloadProps) {
    const { addToast } = useToasts();
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const [actionRow, setActionRow] = React.useState<IClientBooking | null>(null);
    const [selectedAppointmentType, setSelectedAppointmentType] = React.useState("");
    const [dialog, setDialog] = React.useState<Dialogs | null>(null);
    const [selectedRow, setSelectedRow] = React.useState<IClientBooking | null>(
        null
    );
    const [newRequestedService, setNewRequestedService] = React.useState("");
    const olc = React.useContext(CacheContext);
    const allDepartmentsAndServices =
        olc?.allDepartmentAndServices || ({} as DepartmentCache);
    const userAcl = React.useContext(AclContext)?.authUser?.acl;

    const getAppointmentTypeLabel = (type: string) => {
        switch (type) {
            case "recurring_appointment":
                return "Recurring Appointments";
            case "one_off":
                return "One-Off Appointments";
            default:
                return "Show all appointment types";
        }
    };

    const [clientToAddBooking, setClientToAddBooking] = React.useState<
        IClientBooking | undefined
    >();

    const baseClinicianBookings = [
        ...(clinician.confirmed || []),
        ...(clinician.held || []),
    ];

    const supervisorIntakeBookings: (IClientBooking & {
        is_supervisor_intake?: boolean;
        original_booking_id?: string;
    })[] = (clinician.available_for_intake_sessions || [])
        .filter((session) => session.booking_id && session.client_id)
        .map((session) => {
            const bookingId = session.booking_id!;
            const clientId = session.client_id!;

            const supervisorIntakeBooking: IClientBooking & {
                is_supervisor_intake?: boolean;
                original_booking_id?: string;
            } = {
                id: session.id,
                start: session.start_time,
                end: session.end_time,
                recurrenceRule: RecurrenceRule.ONE_OFF,
                starting_date: session.start_time,
                service_delivery_option: session.service_delivery_option,
                location: session.location || "",
                office: session.office || "",
                clinician_display_name: session.supervisees_display_name || "",
                client_display_name: session.client_display_name || "",
                clinician_id: session.supervisor_id as string,
                client_id: clientId,
                status:
                    session.status === "confirmed"
                        ? BookingStatus.CONFIRMED
                        : BookingStatus.HELD,
                requested_service: "Supervisor Intake Session",
                practice_id: session.practice_id || "",
                is_supervisor_intake: true,
                original_booking_id: bookingId,
            };

            return supervisorIntakeBooking;
        });

    const allClinicianBookings = [
        ...baseClinicianBookings,
        ...supervisorIntakeBookings,
    ];

    const stats = getClinicianCaseloadStats(caseloadClinician);
    const sortedEntities = stats.entities
        .map((entity) => ({
            ...entity,
            label:
                findLabelByPracticeId(
                    entity.practiceId,
                    allDepartmentsAndServices
                ) ?? entity.practiceId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    const formatSessions = (value: number) =>
        value.toLocaleString("en-US", { maximumFractionDigits: 2 });

    function getAppointmentCheck(
        clients: { [id: string]: IClient },
        booking: IClientBooking | null
    ) {
        if (!booking) return null;
        return (clients[booking.client_id]?.appointment_checks || {})[booking.id];
    }

    function doesRequireConsultCall(
        clients: { [id: string]: IClient },
        c: IClientBooking
    ) {
        const appointmentCheck = getAppointmentCheck(clients, c);
        const consultCallNeeded =
            appointmentCheck?.client_requested_call &&
            !appointmentCheck?.consult_call_completed;
        return consultCallNeeded;
    }

    const filterAppointmentByType = (type: string) => {
        if (type === "one_off") {
            const oneOffAppointments = [...(allClinicianBookings || [])].filter(
                (booking) =>
                    booking.recurrenceRule === RecurrenceRule.ONE_OFF &&
                    (booking.status === BookingStatus.CONFIRMED ||
                        booking.status === BookingStatus.HELD)
            );
            return oneOffAppointments;
        }
        if (type === "recurring_appointment") {
            const recurringAppointments = [...(allClinicianBookings || [])].filter(
                (booking) =>
                    booking.recurrenceRule !== RecurrenceRule.ONE_OFF &&
                    (booking.status === BookingStatus.CONFIRMED ||
                        booking.status === BookingStatus.HELD)
            );
            return recurringAppointments;
        }
        return allClinicianBookings;
    };

    const [clientFilterOptions, setClientFilterOptions] = React.useState<{
        name: string;
        otherFilters: string;
    }>({
        name: "",
        otherFilters: "",
    });

    const [filteredClientBookings, setFilteredClientBookings] =
        React.useState(allClinicianBookings);

    const [clientForm, setClientForm] = React.useState({
        show: false,
        allowEdit: true,
        client: defaultClient,
        allowChangeStatus: false,
        bookingId: "",
    });

    React.useEffect(() => {
        const filteredAppointments = [
            ...(filterAppointmentByType(selectedAppointmentType) || []),
        ];
        const filtered = filteredAppointments?.filter((c) => {
            const hasFilter =
                clientFilterOptions.name.length >= 2 ||
                clientFilterOptions.otherFilters;

            if (!hasFilter) {
                return (
                    !c.is_paused &&
                    (c.status === BookingStatus.CONFIRMED ||
                        c.status === BookingStatus.HELD)
                );
            }

            let matchesCriteria = false;

            const consultCallNeeded = doesRequireConsultCall(clients, c);
            const client = clients[c.client_id] || {
                name: "",
                first_name: "",
                last_name: "",
            };
            if (
                clientFilterOptions.name.length >= 2 &&
                (c.status === BookingStatus.CONFIRMED ||
                    c.status === BookingStatus.HELD) &&
                `${client.first_name} ${client.last_name}`
                    .toLowerCase()
                    .includes(clientFilterOptions.name.toLowerCase())
            ) {
                matchesCriteria = true;
            }

            if (
                clientFilterOptions.otherFilters ===
                    "show_only_clients_that_require_followup" &&
                c.status === BookingStatus.CONFIRMED &&
                !c.clinician_contacted_client
            ) {
                matchesCriteria = true;
            }

            if (
                clientFilterOptions.otherFilters ===
                    "show_only_clients_with_pending_consult_call" &&
                consultCallNeeded
            ) {
                matchesCriteria = true;
            }

            if (
                clientFilterOptions.otherFilters === "show_only_paused_clients" &&
                c.is_paused
            ) {
                matchesCriteria = true;
            }

            return matchesCriteria;
        });
        setFilteredClientBookings(filtered);
        setCaseLoadCountByFilter(filtered.length);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        clients,
        clientFilterOptions,
        selectedAppointmentType,
        clinician.confirmed,
        clinician.held,
    ]);

    const displayClient = (client: IClient, bookingId: string) => {
        setClientForm({
            client,
            allowEdit: false,
            show: true,
            allowChangeStatus: false,
            bookingId,
        });
    };

    const handleSendServiceTypeChangeEmail = async () => {
        try {
            sendServiceTypeChangeEmail(
                clients[selectedRow?.client_id || ""],
                clinician,
                selectedRow || ({} as IClientBooking),
                allDepartmentsAndServices,
                newRequestedService || ""
            );
            setNewRequestedService("");
            setDialog(null);
            setSelectedRow(null);
            addToast("Email sent to Intake team", {
                appearance: "success",
            });
        } catch (error) {
            console.error(error);
            addToast("Error sending service type change email", {
                appearance: "error",
            });
        }
    };

    const departmentServices = clinicianServiceSelections[clinician.email]?.services
        ? Object.values(clinicianServiceSelections[clinician.email]?.services)
              .filter(
                  (service) =>
                      service?.toLowerCase().replaceAll("_", "") !== "teletherapy"
              )
              .map((service) => ({
                  value: service,
                  label: service
                      ?.replaceAll("_", " ")
                      .replace(/bw/g, (l) => l.toUpperCase()),
              }))
        : [];

    const getClientFilterLabel = (filter: string) => {
        switch (filter) {
            case "show_only_clients_that_require_followup":
                return `Appointments requiring follow-up (${requiresFollowupCount})`;
            case "show_only_clients_with_pending_consult_call":
                return `Appointments with pending consult calls (${pendingConsultCallCount})`;
            case "show_only_paused_clients":
                return `Paused appointments (${pausedClientCount})`;
            default:
                return `Show all appointments (${
                    filterAppointmentByType(selectedAppointmentType)?.length
                })`;
        }
    };

    let requiresFollowupCount = 0;
    let pendingConsultCallCount = 0;
    let pausedClientCount = 0;

    filterAppointmentByType(selectedAppointmentType).forEach((c) => {
        if (c.status === BookingStatus.CONFIRMED && !c.clinician_contacted_client) {
            requiresFollowupCount++;
        }
        if (doesRequireConsultCall(clients, c)) {
            pendingConsultCallCount++;
        }

        if (c.is_paused) {
            pausedClientCount++;
        }
    });

    const columns = React.useMemo(() => {
        const headers: TableHeader[] = [
            {
                Header: "Client",
                id: "client_full_name",
                accessor: (row: IClientBooking) => (
                    <div>
                        <p className={styles.clientInfo}>
                            {`${clients[row.client_id]?.first_name || ""} ${
                                clients[row.client_id]?.last_name[0] || ""
                            }.`}
                            {(isClientMinor(clients[row.client_id]?.date_of_birth) ||
                                isClientElderly(
                                    clients[row.client_id]?.date_of_birth
                                )) && (
                                <span>
                                    <Badge
                                        tone="info"
                                        content={
                                            isClientMinor(
                                                clients[row.client_id]?.date_of_birth
                                            )
                                                ? "Minor"
                                                : "Elderly"
                                        }
                                    />
                                </span>
                            )}
                            {row.is_paused && <PauseIcon />}
                        </p>

                        <Badge
                            content={row.status}
                            textColor={
                                row.status === BookingStatus.CONFIRMED
                                    ? "#1cae60"
                                    : "#dda31b"
                            }
                            bgColor={
                                row.status === BookingStatus.CONFIRMED
                                    ? "rgba(35, 209, 96, 0.15)"
                                    : "#fef8e9"
                            }
                        />
                    </div>
                ),
            },
            {
                Header: "Preferred name",
                id: "preferred_name",
                accessor: (row: IClientBooking) => (
                    <p className={styles.clientInfo}>
                        {clients[row.client_id]?.preferred_name || "--"}
                    </p>
                ),
            },
            {
                Header: "Pronouns",
                id: "client_pronouns",
                accessor: (row: IClientBooking) => (
                    <p>{clients[row.client_id]?.pronouns || "--"}</p>
                ),
            },
            {
                Header: "Email",
                id: "client_email",
                accessor: (row: IClientBooking) => (
                    <p>{clients[row.client_id]?.email || "--"}</p>
                ),
            },
            {
                Header: "Location",
                id: "client_location",
                accessor: (row: IClientBooking) => {
                    const client = clients[row.client_id];
                    const fromState = getStateDisplayName(client?.state);
                    return <p className={styles.clientInfo}>{fromState || "--"}</p>;
                },
            },

            {
                Header: "Phone no",
                id: "client_phone_no",
                accessor: (row: IClientBooking) => (
                    <p>{clients[row.client_id]?.phone_no || "--"}</p>
                ),
            },
            {
                Header: "Scheduled Appointment",
                id: "day",
                accessor: (row: IClientBooking) => {
                    const isSupervisorIntakeView = (row as any).is_supervisor_intake;
                    const isSuperviseeView = row?.supervisor_intake_session;
                    const defaultLabel = `${moment(row.start).format(
                        DateFormats.DAY_TIME_FORMAT
                    )}, ${row.recurrenceRule}`;
                    const superviseeIntakeLabel = `${moment(
                        row.supervisor_intake_session?.start_time
                    ).format(DateFormats.DAY_MONTH_YEAR_FORMAT)} @ ${moment(
                        row.supervisor_intake_session?.start_time
                    ).format(DateFormats.HOUR_MIN_FORMAT)}, ${
                        row.supervisor_intake_session?.recurrence
                    }`;

                    return (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            {defaultLabel}
                            {isSupervisorIntakeView && (
                                <span
                                    className={styles.supervisorIntakeBadge}
                                >{`(Supervisor Intake with ${row.clinician_display_name}'s client)`}</span>
                            )}
                            {isSuperviseeView && (
                                <span
                                    className={styles.supervisorIntakeBadge}
                                >{`(Supervisor Intake with ${row.supervisor_intake_session?.clinician_display_name} on ${superviseeIntakeLabel})`}</span>
                            )}
                        </div>
                    );
                },
            },
            {
                Header: "Practice",
                id: "practice",
                accessor: (row: IClientBooking) => {
                    const appointmentPractice = findLabelByPracticeId(
                        (row.practice_id as string) ||
                            (process.env.REACT_APP_MANTLE_PRACTICE_ID as string),
                        allDepartmentsAndServices
                    );
                    return (
                        <span>
                            <Badge
                                textColor={
                                    getV2BadgePropsByPractice(appointmentPractice)
                                        .textColor
                                }
                                content={appointmentPractice}
                                bgColor={
                                    getV2BadgePropsByPractice(appointmentPractice)
                                        .bgColor
                                }
                            />
                        </span>
                    );
                },
            },
            {
                Header: "Starting on",
                id: "starting_on",
                accessor: (row: IClientBooking) => (
                    <p>
                        {moment(row.starting_date).diff(moment(), "days") > 0
                            ? moment(row.starting_date).format(
                                  DateFormats.DATE_FORMAT
                              )
                            : "Started"}
                    </p>
                ),
            },
            {
                Header: "In-Person or Telemed",
                id: "service_delivery_option",
                accessor: (row: IClientBooking) => (
                    <span>
                        {
                            serviceDeliveryOptionLabels[
                                row.service_delivery_option || ""
                            ]
                        }
                    </span>
                ),
            },
            {
                Header: "",
                id: "actions",
                accessor: (row: IClientBooking) => {
                    const appointmentCheck = getAppointmentCheck(clients, row);
                    const requestedConsultCall =
                        appointmentCheck?.client_requested_call;
                    return (
                        <>
                            {!(row as any).is_supervisor_intake ? (
                                <OverlayTrigger
                                    trigger="click"
                                    placement="bottom-end"
                                    flip
                                    rootClose
                                    show={actionRow === row}
                                    onToggle={(show) =>
                                        setActionRow(show ? row : null)
                                    }
                                    overlay={
                                        <Popover
                                            id="caseload-row-actions"
                                            className={styles.actionsPopover}
                                            aria-label="Row actions"
                                        >
                                            <Popover.Content
                                                className={
                                                    styles.actionsDropdownContent
                                                }
                                                onClick={() => setActionRow(null)}
                                            >
                                                <div
                                                    className={
                                                        styles.caseloadActions
                                                    }
                                                >
                                                    {row.status ===
                                                        BookingStatus.CONFIRMED && (
                                                        <>
                                                            <Button
                                                                variant="normal"
                                                                type="button"
                                                                onClick={() => {
                                                                    setDialog(
                                                                        "cancel_session"
                                                                    );
                                                                    setSelectedRow(
                                                                        row
                                                                    );
                                                                }}
                                                            >
                                                                Cancel Sessions
                                                            </Button>
                                                            <Button
                                                                variant="normal"
                                                                type="button"
                                                                onClick={async () => {
                                                                    await store.updateBooking(
                                                                        row.clinician_id,
                                                                        row.client_id,
                                                                        row.id,
                                                                        {
                                                                            clinician_contacted_client:
                                                                                true,
                                                                        },
                                                                        {},
                                                                        "confirmed",
                                                                        loggedInUserEmail!
                                                                    );

                                                                    await onUpdateCaseLoad();
                                                                    addToast(
                                                                        "Updated",
                                                                        {
                                                                            appearance:
                                                                                "success",
                                                                        }
                                                                    );
                                                                }}
                                                            >
                                                                Sent Introduction
                                                                Email to New Client
                                                            </Button>

                                                            {!Boolean(
                                                                clients[
                                                                    row.client_id
                                                                ]
                                                            ) && (
                                                                <Button
                                                                    variant="normal"
                                                                    type="button"
                                                                    onClick={async () => {
                                                                        await store.deleteConfirmedAppointmentFromClinician(
                                                                            {
                                                                                clinicianId:
                                                                                    clinician.id ||
                                                                                    clinician.email,
                                                                                appointmentId:
                                                                                    row.id,
                                                                            }
                                                                        );
                                                                        await onUpdateCaseLoad();
                                                                        addToast(
                                                                            "Appointment entry deleted",
                                                                            {
                                                                                appearance:
                                                                                    "success",
                                                                            }
                                                                        );
                                                                    }}
                                                                >
                                                                    Delete entry
                                                                </Button>
                                                            )}

                                                            {!row.is_paused && (
                                                                <Button
                                                                    variant="normal"
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setClientToAddBooking(
                                                                            row
                                                                        );
                                                                    }}
                                                                >
                                                                    Add additional
                                                                    appointment
                                                                </Button>
                                                            )}
                                                        </>
                                                    )}
                                                    {requestedConsultCall && (
                                                        <Button
                                                            variant="normal"
                                                            type="button"
                                                            onClick={() => {
                                                                setDialog(
                                                                    "view_consult_call"
                                                                );
                                                                setSelectedRow(row);
                                                            }}
                                                        >
                                                            View consult call options
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="normal"
                                                        type="button"
                                                        onClick={async () => {
                                                            const is_paused =
                                                                !Boolean(
                                                                    row.is_paused
                                                                );
                                                            await store.updateBooking(
                                                                row.clinician_id,
                                                                row.client_id,
                                                                row.id,
                                                                {
                                                                    is_paused,
                                                                    date_paused:
                                                                        is_paused
                                                                            ? new Date().toISOString()
                                                                            : "",
                                                                },
                                                                {},
                                                                row.status ===
                                                                    BookingStatus.CONFIRMED
                                                                    ? "confirmed"
                                                                    : "held",
                                                                loggedInUserEmail!
                                                            );
                                                            await onUpdateCaseLoad();
                                                            const messagePrefix =
                                                                row.is_paused
                                                                    ? "resumed"
                                                                    : "paused";

                                                            addToast(
                                                                `Client ${messagePrefix}`,
                                                                {
                                                                    appearance:
                                                                        "success",
                                                                }
                                                            );
                                                        }}
                                                    >
                                                        {`${
                                                            row.is_paused
                                                                ? "Resume"
                                                                : "Pause"
                                                        } client sessions`}
                                                    </Button>
                                                    {row.service_delivery_option ===
                                                        "in_person" && (
                                                        <Button
                                                            variant="normal"
                                                            type="button"
                                                            onClick={() => {
                                                                setDialog(
                                                                    "switch_to_telemed"
                                                                );
                                                                setSelectedRow(row);
                                                            }}
                                                        >
                                                            Switch to Telemed
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="normal"
                                                        type="button"
                                                        onClick={() => {
                                                            setDialog(
                                                                "request_service_type_change"
                                                            );
                                                            setSelectedRow(row);
                                                        }}
                                                    >
                                                        Request Service Type Change
                                                    </Button>
                                                </div>
                                            </Popover.Content>
                                        </Popover>
                                    }
                                >
                                    <Button
                                        aria-expanded={actionRow === row}
                                        iconRight={
                                            <ChevronDownIcon stroke="var(--color-white)" />
                                        }
                                        className={styles.caseloadActionButton}
                                    >
                                        Actions
                                    </Button>
                                </OverlayTrigger>
                            ) : (
                                "--"
                            )}
                        </>
                    );
                },
            },
        ];
        return headers;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        actionRow,
        clients,
        onUpdateCaseLoad,
        clinician,
        addToast,
        loggedInUserEmail,
    ]);

    return (
        <div className={styles.caseload}>
            <ClinicianAddBooking
                prevBooking={clientToAddBooking}
                onCancel={() => {
                    setClientToAddBooking(undefined);
                }}
                onComplete={async () => {
                    await onUpdateCaseLoad();
                    setClientToAddBooking(undefined);
                }}
                loggedInClinician={clinician}
            />
            {clientForm.show && (
                <Dialog
                    open={clientForm.show}
                    onClose={() => {
                        setClientForm({ ...clientForm, show: false });
                    }}
                    title={`Cancel sessions for ${clientForm.client?.first_name} ${clientForm.client?.last_name}`}
                    showFooter={false}
                    onCancel={() => {
                        setClientForm({ ...clientForm, show: false });
                    }}
                >
                    <ClientDisplayDialog
                        clientDialogOptions={{
                            ...clientForm,
                            onHide: () => {
                                setClientForm({ ...clientForm, show: false });
                            },
                            onSaveClient: async (c) => {
                                setClientForm({ ...clientForm, show: false });
                                await onUpdateCaseLoad();
                            },
                            bookingId: clientForm.bookingId,
                            expectedCaller: userAcl?.super_admin
                                ? "admin"
                                : "clinician",
                        }}
                    />
                </Dialog>
            )}
            <Dialog
                open={
                    dialog === "request_service_type_change" && selectedRow !== null
                }
                onClose={() => setDialog(null)}
                title="Request Service Type Change"
                onCancel={() => setDialog(null)}
                onSave={handleSendServiceTypeChangeEmail}
                saveText="Send"
                cancelText="Cancel"
                isSubmitBtnDisabled={!newRequestedService}
            >
                <div className="v2-form">
                    <Select
                        label="New Service"
                        options={departmentServices}
                        value={departmentServices.find(
                            (option) => option.value === newRequestedService
                        )}
                        onChange={(value) => {
                            setNewRequestedService((value as Option).value);
                        }}
                        isLongListInDialog
                    />
                </div>
            </Dialog>
            <AlertDialog
                open={dialog === "cancel_session" && selectedRow !== null}
                onClose={() => setDialog(null)}
                title="Delete schedule"
                description={
                    <div>
                        Have you completed a termination note for this client? If you
                        do not write the termination note before you terminate the
                        client, you won't be able to do that on Mantle
                    </div>
                }
                onSaveClick={async () => {
                    displayClient(
                        clients[selectedRow?.client_id || ""],
                        selectedRow?.id || ""
                    );
                    setDialog(null);
                }}
                onCancelClick={() => {
                    setDialog(null);
                    setClientForm({
                        ...clientForm,
                        show: false,
                    });
                }}
                cancelText="No, I need to write the note"
                saveText="Yes, I have written the note"
            />
            <AlertDialog
                open={dialog === "switch_to_telemed" && selectedRow !== null}
                onClose={() => setDialog(null)}
                title="Confirm switch to Telemed"
                description={
                    <div>
                        Are you sure you want to switch this client to Telemed
                        sessions? You cannot switch them back to In-Person sessions
                        after this change. Only the Intake team is authorised to
                        switch clients from Telemed sessions to In-Person sessions
                    </div>
                }
                onSaveClick={async () => {
                    await store.updateBooking(
                        selectedRow?.clinician_id || "",
                        selectedRow?.client_id || "",
                        selectedRow?.id || "",
                        {
                            service_delivery_option: "remote",
                        },
                        {},
                        selectedRow?.status === BookingStatus.CONFIRMED
                            ? "confirmed"
                            : "held",
                        loggedInUserEmail!
                    );
                    await onUpdateCaseLoad();
                    addToast("Client appointment changed", {
                        appearance: "success",
                    });
                    setDialog(null);
                }}
                onCancelClick={() => {
                    setDialog(null);
                }}
                saveText="Switch to Telemed"
            />

            <AlertDialog
                open={dialog === "view_consult_call" && selectedRow !== null}
                onClose={() => setDialog(null)}
                title={`Consult notes for ${selectedRow?.client_display_name}`}
                description={
                    <div>
                        {getAppointmentCheck(clients, selectedRow)
                            ?.consult_call_notes || ""}
                    </div>
                }
                onSaveClick={async () => {
                    const appointmentCheck = getAppointmentCheck(
                        clients,
                        selectedRow
                    );
                    if (appointmentCheck && selectedRow) {
                        await store.updateBooking(
                            selectedRow.clinician_id,
                            selectedRow.client_id,
                            selectedRow.id,
                            {},
                            {
                                consult_call_completed: true,
                                consult_call_completed_on: new Date().toISOString(),
                            },
                            selectedRow.status === BookingStatus.CONFIRMED
                                ? "confirmed"
                                : "held",
                            loggedInUserEmail!
                        );
                        await onUpdateCaseLoad();
                        addToast("Consult call marked as completed", {
                            appearance: "success",
                        });
                    }
                    setDialog(null);
                }}
                onCancelClick={() => {
                    setDialog(null);
                }}
                saveText="Mark as completed"
            />
            <section className={styles.overview} aria-label="Caseload overview">
                <span className={styles.overviewLabel}>Including paused:</span>
                <span className={styles.clientMetric}>
                    <span className={styles.overviewLabel}>Total clients:</span>
                    <strong>{stats.clients}</strong>
                    {sortedEntities.length > 0 && (
                        <span>
                            (
                            {sortedEntities.map((entity, index) => (
                                <React.Fragment key={entity.practiceId}>
                                    {index > 0 && " · "}
                                    <span className={styles.entityCount}>
                                        {entity.clients} {entity.label}
                                    </span>
                                </React.Fragment>
                            ))}
                            )
                        </span>
                    )}
                </span>
                <span className={styles.clientMetric}>
                    <OverlayTrigger
                        placement="top"
                        trigger={["hover", "focus"]}
                        overlay={
                            <Tooltip id="weekly-session-breakdown">
                                <div className={styles.weeklyBreakdown}>
                                    <strong>Weekly session breakdown</strong>
                                    {stats.frequencies.map((frequency) => (
                                        <div key={frequency.recurrence}>
                                            {frequency.recurrence}:{" "}
                                            {frequency.bookings}
                                            {" × "}
                                            {Number.isInteger(frequency.weight * 100)
                                                ? formatSessions(frequency.weight)
                                                : `1/${Math.round(
                                                      1 / frequency.weight
                                                  )}`}
                                            {" = "}
                                            {formatSessions(
                                                frequency.weeklySessions
                                            )}
                                        </div>
                                    ))}
                                    <div>
                                        <strong>
                                            Total:{" "}
                                            {formatSessions(stats.weeklySessions)}{" "}
                                            weekly
                                        </strong>
                                    </div>
                                </div>
                            </Tooltip>
                        }
                    >
                        <button
                            type="button"
                            className={styles.weeklyLabel}
                            aria-label={`${formatSessions(
                                stats.weeklySessions
                            )} weekly sessions, show breakdown`}
                        >
                            <span className={styles.overviewLabel}>
                                Weekly sessions:
                            </span>
                            <strong>{formatSessions(stats.weeklySessions)}</strong>
                            <InfoIcon width="14" height="14" stroke="currentColor" />
                        </button>
                    </OverlayTrigger>
                    {sortedEntities.length > 0 && (
                        <span>
                            (
                            {sortedEntities.map((entity, index) => (
                                <React.Fragment key={entity.practiceId}>
                                    {index > 0 && " · "}
                                    <span className={styles.entityCount}>
                                        {formatSessions(entity.weeklySessions)}{" "}
                                        {entity.label}
                                    </span>
                                </React.Fragment>
                            ))}
                            )
                        </span>
                    )}
                </span>
                <span className={styles.overviewMetric}>
                    <span className={styles.overviewLabel}>Ideal caseload:</span>
                    {stats.percentFull == null ? (
                        "Target not set"
                    ) : (
                        <>
                            <strong>{stats.idealCaseload}</strong> weekly sessions
                        </>
                    )}
                </span>
                {stats.percentFull != null && (
                    <span className={styles.overviewMetric}>
                        <OverlayTrigger
                            placement="top"
                            trigger={["hover", "focus"]}
                            overlay={
                                <Tooltip id="caseload-fullness-calculation">
                                    <div className={styles.weeklyBreakdown}>
                                        <strong>Fullness calculation</strong>
                                        <div>
                                            Weekly sessions ÷ ideal caseload × 100
                                        </div>
                                        <div>
                                            {formatSessions(stats.weeklySessions)} ÷{" "}
                                            {stats.idealCaseload} × 100 ≈{" "}
                                            {stats.percentFull}%
                                        </div>
                                    </div>
                                </Tooltip>
                            }
                        >
                            <button
                                type="button"
                                className={styles.weeklyLabel}
                                aria-label={`Fullness: ${stats.percentFull}%, show calculation`}
                            >
                                <span className={styles.overviewLabel}>
                                    Fullness:
                                </span>
                                <strong>{stats.percentFull}%</strong>
                                <InfoIcon
                                    width="14"
                                    height="14"
                                    stroke="currentColor"
                                />
                            </button>
                        </OverlayTrigger>
                    </span>
                )}
                <span className={styles.overviewMetric}>
                    <span className={styles.overviewLabel}>Clients on hold:</span>
                    <strong>{stats.heldClients}</strong>
                </span>
            </section>
            <div className={styles.filters}>
                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton
                            text={getAppointmentTypeLabel(selectedAppointmentType)}
                        />
                    </Dropdown.Trigger>
                    <Dropdown.Content>
                        <>
                            <Dropdown.Item
                                closeMenuOnSelect
                                isSelected={selectedAppointmentType === ""}
                                onClick={() => {
                                    setSelectedAppointmentType("");
                                }}
                            >
                                Show all appointment types
                            </Dropdown.Item>
                            <Dropdown.Item
                                closeMenuOnSelect
                                isSelected={
                                    selectedAppointmentType ===
                                    "recurring_appointment"
                                }
                                onClick={() => {
                                    setSelectedAppointmentType(
                                        "recurring_appointment"
                                    );
                                }}
                            >
                                Recurring Appointments
                            </Dropdown.Item>
                            <Dropdown.Item
                                closeMenuOnSelect
                                isSelected={selectedAppointmentType === "one_off"}
                                onClick={() => {
                                    setSelectedAppointmentType("one_off");
                                }}
                            >
                                One-Off Appointments
                            </Dropdown.Item>
                        </>
                    </Dropdown.Content>
                </Dropdown>
                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton
                            text={getClientFilterLabel(
                                clientFilterOptions.otherFilters
                            )}
                        />
                    </Dropdown.Trigger>
                    <Dropdown.Content>
                        <div className={styles.clientFilterOptions}>
                            <Dropdown.Item
                                closeMenuOnSelect
                                isSelected={clientFilterOptions.otherFilters === ""}
                                onClick={() => {
                                    setClientFilterOptions((prevOptions) => {
                                        return {
                                            ...prevOptions,
                                            otherFilters: "",
                                        };
                                    });
                                }}
                            >
                                {`Show all appointments (${
                                    filterAppointmentByType(selectedAppointmentType)
                                        ?.length
                                })`}
                            </Dropdown.Item>
                            <Dropdown.Item
                                closeMenuOnSelect
                                isSelected={
                                    clientFilterOptions.otherFilters ===
                                    "show_only_clients_that_require_followup"
                                }
                                onClick={() => {
                                    setClientFilterOptions((prevOptions) => {
                                        return {
                                            ...prevOptions,
                                            otherFilters:
                                                "show_only_clients_that_require_followup",
                                        };
                                    });
                                }}
                            >
                                {`Appointments requiring follow-up (${requiresFollowupCount})`}
                            </Dropdown.Item>
                            <Dropdown.Item
                                closeMenuOnSelect
                                isSelected={
                                    clientFilterOptions.otherFilters ===
                                    "show_only_clients_with_pending_consult_call"
                                }
                                onClick={() => {
                                    setClientFilterOptions((prevOptions) => {
                                        return {
                                            ...prevOptions,
                                            otherFilters:
                                                "show_only_clients_with_pending_consult_call",
                                        };
                                    });
                                }}
                            >
                                {`Appointments with pending consult calls (${pendingConsultCallCount})`}
                            </Dropdown.Item>
                            <Dropdown.Item
                                closeMenuOnSelect
                                isSelected={
                                    clientFilterOptions.otherFilters ===
                                    "show_only_paused_clients"
                                }
                                onClick={() => {
                                    setClientFilterOptions((prevOptions) => {
                                        return {
                                            ...prevOptions,
                                            otherFilters: "show_only_paused_clients",
                                        };
                                    });
                                }}
                            >
                                {`Paused appointments (${pausedClientCount})`}
                            </Dropdown.Item>
                        </div>
                    </Dropdown.Content>
                </Dropdown>
                <SearchInput
                    placeholder="Filter by client name"
                    value={clientFilterOptions.name}
                    onChange={(e) => {
                        const value = `${e.target.value}`;
                        setClientFilterOptions((prevOptions) => {
                            return {
                                ...prevOptions,
                                name: value,
                            };
                        });
                    }}
                />
            </div>
            {filteredClientBookings.length === 0 ? (
                <EmptyListState emptyMessage="No appointments found" />
            ) : (
                <Table
                    columns={columns}
                    data={filteredClientBookings}
                    allowSelect={false}
                    hasActionsColumn={true}
                    stickyLastColumn
                />
            )}
        </div>
    );
}
